import {
  compressImage,
  getOptimalCompressionSettings,
  type CompressionResult,
} from '@/utils/photoCompression';

export interface UploadProgressCallback {
  onProgress: (progress: number) => void;
  onCompressionStart: () => void;
  onCompressionComplete: (result: CompressionResult) => void;
  onUploadStart: () => void;
  onUploadComplete: (response: any) => void;
  onError: (error: Error) => void;
}

export interface UploadConfig {
  endpoint: string;
  method?: 'POST' | 'PUT';
  headers?: Record<string, string>;
  fieldName?: string; // Field name for form data
  additionalData?: Record<string, any>;
}

/**
 * Upload a photo with compression and progress tracking
 */
export const uploadPhotoWithProgress = async (
  file: File,
  config: UploadConfig,
  callbacks: UploadProgressCallback
): Promise<any> => {
  try {
    // Step 1: Compression
    callbacks.onCompressionStart();

    const compressionSettings = getOptimalCompressionSettings(file.size);
    const compressionResult = await compressImage(file, compressionSettings);

    callbacks.onCompressionComplete(compressionResult);

    // Step 2: Upload
    callbacks.onUploadStart();

    const response = await uploadBlobWithProgress(
      compressionResult.blob,
      file.name,
      config,
      callbacks.onProgress
    );

    callbacks.onUploadComplete(response);
    return response;
  } catch (error) {
    const err = error instanceof Error ? error : new Error(String(error));
    callbacks.onError(err);
    throw err;
  }
};

/**
 * Upload blob with XMLHttpRequest for progress tracking
 */
const uploadBlobWithProgress = (
  blob: Blob,
  fileName: string,
  config: UploadConfig,
  onProgress: (progress: number) => void
): Promise<any> => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    const formData = new FormData();

    // Prepare form data
    const fieldName = config.fieldName || 'file';
    formData.append(fieldName, blob, fileName);

    // Add additional data to form
    if (config.additionalData) {
      Object.entries(config.additionalData).forEach(([key, value]) => {
        formData.append(
          key,
          typeof value === 'string' ? value : JSON.stringify(value)
        );
      });
    }

    // Track upload progress
    xhr.upload.addEventListener('progress', event => {
      if (event.lengthComputable) {
        const progress = (event.loaded / event.total) * 100;
        onProgress(progress);
      }
    });

    // Handle completion
    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const response = JSON.parse(xhr.responseText);
          resolve(response);
        } catch {
          resolve(xhr.responseText);
        }
      } else {
        reject(new Error(`HTTP ${xhr.status}: ${xhr.statusText}`));
      }
    });

    // Handle errors
    xhr.addEventListener('error', () => {
      reject(new Error('Network error occurred'));
    });

    xhr.addEventListener('abort', () => {
      reject(new Error('Upload was aborted'));
    });

    // Set headers
    if (config.headers) {
      Object.entries(config.headers).forEach(([key, value]) => {
        xhr.setRequestHeader(key, value);
      });
    }

    // Set timeout for file uploads (5 minutes)
    xhr.timeout = 300000;

    // Handle timeout
    xhr.addEventListener('timeout', () => {
      reject(
        new Error(
          'Upload timeout - please check your internet connection and try again'
        )
      );
    });

    // Start upload
    xhr.open(config.method || 'POST', config.endpoint);
    xhr.send(formData);
  });
};

/**
 * Upload photo as base64 (existing API format) with compression
 */
export const uploadPhotoAsBase64WithProgress = async (
  file: File,
  siteId: number,
  callbacks: UploadProgressCallback
): Promise<any> => {
  try {
    // Step 1: Compression
    callbacks.onCompressionStart();

    const compressionSettings = getOptimalCompressionSettings(file.size);
    const compressionResult = await compressImage(file, compressionSettings);

    callbacks.onCompressionComplete(compressionResult);

    // Step 2: Upload as base64
    callbacks.onUploadStart();

    // Extract base64 data from data URL (remove prefix if present)
    const base64Data = compressionResult.dataUrl.includes(',')
      ? compressionResult.dataUrl.split(',')[1]
      : compressionResult.dataUrl;

    const response = await uploadBase64WithProgress(
      base64Data,
      siteId,
      callbacks.onProgress
    );

    callbacks.onUploadComplete(response);
    return response;
  } catch (error) {
    const err = error instanceof Error ? error : new Error(String(error));
    callbacks.onError(err);
    throw err;
  }
};

/**
 * Upload base64 data with progress simulation (since base64 uploads are instant)
 */
const uploadBase64WithProgress = async (
  base64Data: string,
  siteId: number,
  onProgress: (progress: number) => void
): Promise<any> => {
  // Import the sites API service dynamically to avoid circular imports
  const { sitesApi } = await import('@/features/sites/services');

  // Simulate progress for base64 upload
  const progressSteps = [10, 30, 60, 90, 100];
  let currentStep = 0;

  const progressInterval = setInterval(() => {
    if (currentStep < progressSteps.length - 1) {
      onProgress(progressSteps[currentStep]);
      currentStep++;
    } else {
      clearInterval(progressInterval);
    }
  }, 100);

  try {
    const response = await sitesApi.addPhoto({
      siteId,
      photoData: base64Data,
    });

    clearInterval(progressInterval);
    onProgress(100);

    return response;
  } catch (error) {
    clearInterval(progressInterval);
    throw error;
  }
};

/**
 * Batch upload multiple photos with individual progress tracking
 */
export const uploadMultiplePhotos = async (
  files: File[],
  siteId: number,
  onFileProgress: (fileIndex: number, progress: number, status: string) => void,
  onFileComplete: (fileIndex: number, result: any) => void,
  onFileError: (fileIndex: number, error: Error) => void
): Promise<any[]> => {
  const results: any[] = [];

  for (let i = 0; i < files.length; i++) {
    const file = files[i];

    try {
      const result = await uploadPhotoAsBase64WithProgress(file, siteId, {
        onCompressionStart: () => onFileProgress(i, 0, 'compressing'),
        onCompressionComplete: () => onFileProgress(i, 50, 'uploading'),
        onUploadStart: () => onFileProgress(i, 60, 'uploading'),
        onUploadComplete: response => {
          onFileProgress(i, 100, 'success');
          onFileComplete(i, response);
        },
        onProgress: progress => {
          // Map upload progress to 60-100% range
          const mappedProgress = 60 + progress * 0.4;
          onFileProgress(i, mappedProgress, 'uploading');
        },
        onError: error => {
          onFileError(i, error);
        },
      });

      results.push(result);
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      onFileError(i, err);
      results.push(null);
    }
  }

  return results;
};
